package com.flowyride.core.domain.usecase

import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.gpx.GpxFormatException
import com.flowyride.core.domain.gpx.GpxReader
import com.flowyride.core.domain.gpx.GpxWaypoint
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.util.suspendRunCatching

/** A line from a GPX file, followed as is: no engine touches it. */
data class ImportedTrack(
    val name: String,
    val route: Route,
    /** The file's waypoints (`wpt`), shown along the line. */
    val waypoints: List<GpxWaypoint>,
)

/** What an imported file offers. */
sealed interface GpxContent {
    /** At least one line: the rider picks one when there are several. */
    data class Tracks(val tracks: List<ImportedTrack>) : GpxContent

    /** Only points (`rte` or `wpt`): FlowyRide computes the line between them. */
    data class PointsOnly(val name: String?, val points: List<GpxWaypoint>) : GpxContent
}

class ReadGpxUseCase {

    /**
     * @param fileName used to name the ride when the file does not.
     * @return failure with [GpxFormatException] when the file is not a GPX or holds nothing to ride.
     */
    suspend operator fun invoke(fileName: String, content: String): Result<GpxContent> = suspendRunCatching {
        val file = GpxReader.read(content)
        val fallbackName = file.name ?: fileName.substringBeforeLast('.').ifBlank { null }
        when {
            file.tracks.isNotEmpty() -> GpxContent.Tracks(
                file.tracks.mapIndexed { index, track ->
                    val name = track.name
                        ?: fallbackName?.let { if (file.tracks.size > 1) "$it (${index + 1})" else it }
                        ?: "Trace ${index + 1}"
                    ImportedTrack(name, importedRoute(track.points), file.waypoints)
                },
            )
            // A route is the file's intended order; loose waypoints come last.
            file.routes.isNotEmpty() -> file.routes.first().let { GpxContent.PointsOnly(it.name ?: fallbackName, it.points) }
            file.waypoints.size >= 2 -> GpxContent.PointsOnly(fallbackName, file.waypoints)
            else -> throw GpxFormatException("No track, route nor waypoints")
        }
    }

    private fun importedRoute(track: List<com.flowyride.core.domain.model.TrackPoint>): Route {
        val distance = track.zipWithNext { a, b -> a.point.distanceTo(b.point) }.sum()
        return Route(
            engineId = ENGINE_ID,
            track = track,
            distanceMeters = distance,
            durationSeconds = distance / AVERAGE_SPEED_METERS_PER_SECOND,
        )
    }

    companion object {
        /** Engine of imported lines: nothing computed them. */
        val ENGINE_ID = RouteEngineId("gpx")

        /** Same estimate as loops: measured ~34 km/h on TomTom "thrilling" rides. */
        private const val AVERAGE_SPEED_METERS_PER_SECOND = 35_000.0 / 3_600.0
    }
}
